import { createIdempotencyKey } from "@lafayette/api-client";
import type { StaffCatalogItemDetail, StaffCatalogItemType } from "@lafayette/schemas";
import { useDataProvider } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";

import { AsyncState } from "../../ui/async-state";
import { FieldGroup } from "../../ui/fields";
import { BackLink } from "../../ui/links";
import { PageHeader } from "../../ui/page-header";
import { RadioInput, RadioRow } from "../../ui/radio";
import { SectionCard } from "../../ui/section-card";
import { SectionForm } from "../../ui/section-form";
import { useCatalogCapabilities } from "../access";
import type { SaveMessage } from "../commands";
import { catalogResources } from "../data-provider";
import { describeCatalogError } from "../errors";
import {
  type CommonValues,
  createValuesToInput,
  emptyCommonValues,
  emptySubtypeValues,
  type SubtypeValues,
} from "../form-mappers";
import { itemTypeDescriptions, itemTypeLabels, itemTypeOptions } from "../labels";
import { catalogKeys } from "../queries";
import { CommonFields, commonFieldLabels, commonFieldOrder } from "../sections/CommonFields";
import { subtypeFieldLabels, subtypeSections } from "../sections/subtypes/registry";
import { useReportDirty, useWithoutPrompt } from "../unsaved";
import type { FieldErrors } from "../validation";

type Draft = Readonly<{ type: StaffCatalogItemType; common: CommonValues; subtype: SubtypeValues }>;

function freshDraft(type: StaffCatalogItemType): Draft {
  return { type, common: emptyCommonValues(), subtype: emptySubtypeValues(type) };
}

/** Create a draft item: pick the type, fill the common fields and the type's own fields, create. */
export function CatalogCreatePage() {
  const { capabilities, isLoading } = useCatalogCapabilities();
  const getDataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const withoutPrompt = useWithoutPrompt();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<{ common: FieldErrors; subtype: FieldErrors }>({
    common: {},
    subtype: {},
  });
  const [revision, setRevision] = useState(0);
  const [message, setMessage] = useState<SaveMessage | null>(null);
  const [pending, setPending] = useState(false);
  const key = useRef<{ signature: string; value: string } | null>(null);

  const dirty =
    draft !== null &&
    JSON.stringify([draft.common, draft.subtype]) !==
      JSON.stringify([emptyCommonValues(), emptySubtypeValues(draft.type)]);
  useReportDirty("create", dirty);

  if (isLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.edit) {
    return (
      <AsyncState
        kind="error"
        title="You cannot create catalog items"
        message="Your staff role has read-only access to the catalog."
        accessory={<BackLink to="/catalog">Back to the catalog</BackLink>}
      />
    );
  }

  async function submit() {
    if (draft === null) return;
    const mapped = createValuesToInput(draft.type, draft.common, draft.subtype);
    if (!mapped.ok) {
      setErrors(mapped.errors);
      setRevision((current) => current + 1);
      setMessage(null);
      return;
    }
    setErrors({ common: {}, subtype: {} });
    const signature = JSON.stringify(mapped.value);
    const idempotencyKey =
      key.current?.signature === signature ? key.current.value : createIdempotencyKey();
    key.current = { signature, value: idempotencyKey };
    setPending(true);
    setMessage(null);
    try {
      const response = await getDataProvider().create({
        resource: catalogResources.items,
        variables: mapped.value,
        meta: { idempotencyKey },
      });
      const detail = response.data as unknown as StaffCatalogItemDetail;
      key.current = null;
      queryClient.setQueryData(catalogKeys.item(detail.item.id), detail);
      void queryClient.invalidateQueries({ queryKey: ["catalog", "list"] });
      // The new draft is saved, so leaving this page loses nothing: no prompt.
      withoutPrompt(() =>
        navigate(`/catalog/items/${encodeURIComponent(detail.item.id)}`, {
          state: { created: true },
        }),
      );
    } catch (error) {
      const failure = describeCatalogError(error);
      if (!failure.retryable) key.current = null;
      setMessage({
        tone: "error",
        text:
          failure.kind === "conflict"
            ? "An item with this slug already exists. Choose another slug."
            : failure.message,
      });
    } finally {
      setPending(false);
    }
  }

  const TypeFields = draft === null ? null : subtypeSections[draft.type];
  const errorFields = [
    ...commonFieldOrder
      .filter((field) => errors.common[field] !== undefined)
      .map((field) => ({ id: `create-common-${field}`, label: commonFieldLabels[field] })),
    ...(draft === null
      ? []
      : Object.entries(subtypeFieldLabels[draft.type])
          .filter(([field]) => errors.subtype[field] !== undefined)
          .map(([field, label]) => ({ id: `create-type-${field}`, label }))),
  ];

  return (
    <div className="grid min-w-0 gap-6">
      <PageHeader
        title="New catalog item"
        description="A new item starts as a draft. Customers cannot see it until it is published."
        breadcrumbs={<BackLink to="/catalog">Back to the catalog</BackLink>}
      />

      <SectionCard id="item-type" title="1. Choose the type">
        <FieldGroup
          bare
          legend="Type of item"
          hint="The type decides which fields come next. It cannot change later."
        >
          <div className="mt-1 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,9.5rem),1fr))]">
            {itemTypeOptions.map((option) => (
              <RadioRow
                key={option.value}
                className="grid min-h-11 cursor-pointer grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-x-3 gap-y-0.5 rounded-md border border-border bg-card px-4 py-3 has-[:checked]:border-primary has-[:checked]:bg-accent has-[:checked]:shadow-[inset_0_0_0_1px_var(--color-primary)] compact:min-h-8 compact:py-2"
              >
                <RadioInput
                  name="item-type"
                  value={option.value}
                  className="mt-0.5"
                  checked={draft?.type === option.value}
                  onChange={() => {
                    setErrors({ common: {}, subtype: {} });
                    setDraft((current) =>
                      current === null
                        ? freshDraft(option.value)
                        : {
                            ...current,
                            type: option.value,
                            subtype: emptySubtypeValues(option.value),
                          },
                    );
                  }}
                />
                <span className="text-[15px] font-semibold">{itemTypeLabels[option.value]}</span>
                <span className="col-start-2 text-[13px] leading-[18px] text-muted-foreground">
                  {itemTypeDescriptions[option.value]}
                </span>
              </RadioRow>
            ))}
          </div>
        </FieldGroup>
      </SectionCard>

      {draft === null || TypeFields === null ? null : (
        <SectionCard
          id="item-fields"
          title={`2. Describe the ${itemTypeLabels[draft.type].toLowerCase()}`}
        >
          <div data-fields="two" className="grid">
            <SectionForm
              label="New catalog item"
              dirty={dirty}
              pending={pending}
              readOnly={false}
              message={message}
              errorFields={errorFields}
              errorRevision={revision}
              onSubmit={() => void submit()}
              onDiscard={() => setDraft(freshDraft(draft.type))}
              saveLabel="Create draft"
              alwaysSubmittable
            >
              <h3 className="text-base font-semibold">Details</h3>
              <CommonFields
                idPrefix="create-common"
                values={draft.common}
                errors={errors.common}
                disabled={pending}
                allowBlankFulfilment
                onChange={(field, value) =>
                  setDraft((current) =>
                    current === null
                      ? current
                      : { ...current, common: { ...current.common, [field]: value } },
                  )
                }
              />
              <h3 className="text-base font-semibold">{itemTypeLabels[draft.type]} details</h3>
              <TypeFields
                idPrefix="create-type"
                values={draft.subtype}
                errors={errors.subtype}
                disabled={pending}
                onChange={(field, value) =>
                  setDraft((current) =>
                    current === null
                      ? current
                      : { ...current, subtype: { ...current.subtype, [field]: value } },
                  )
                }
              />
              <p className="m-0 text-sm text-muted-foreground">
                After the draft is created you can add the price, categories, tags, people and
                images.
              </p>
            </SectionForm>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
